<script setup>
import { onBeforeUnmount, ref, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useWindowSize } from '@vueuse/core'
import { useShopify } from '@/stores/shopify'
import { asset_host, minuteVersion } from '@/settings/settings'
import PriceDisplay from './PriceDisplay.vue'
import AddToCartButton from './AddToCartButton.vue'
import ProductMedia from './ProductMedia.vue'
import SelectDropdown from '../atoms/form/SelectDropdown.vue'
import Splide from '@splidejs/splide'
import ProductRendererStandalone from '@/components/3D/ProductRendererStandalone.vue'
import ProductsRelated from '@/components/Shopify/ProductsRelated.vue'
import { useCart } from '@/router/cart'
import { useDataStore } from '@/stores/data.store'
import ButtonDefault from '../atoms/ButtonDefault.vue'
import ProductsScroller from './ProductsScroller.vue'
import ProductQuantityChanger from './ProductQuantityChanger.vue';
import { useShare } from '@/Composables/use-share'

const shopify = useShopify()

const props = defineProps({
  product: {
    type: Object
  },
  imageWidth: {
    type: Number,
    default: 500
  },
  imageHeight: {
    type: Number,
    default: 500
  },
  layoutMode: {
    type: String,
    default: 'side-sheet'
  }
})

const { openCart } = useCart()

const router = useRouter()
const { width: screenWidth } = useWindowSize()
const {share, canShare} = useShare()

const _loading = ref(false)
const _product = ref(null)

const sliderMainRef = ref()
const sliderMain = ref(null)
const sliderReady = ref(false)
const images = ref([])

const price = ref(null)
const compareAtPrice = ref(null)
const variant = ref(null)
const availableForSale = ref(true)
const quantityAvailable = ref(0)
const options = ref({})
const productAdded = ref(false)
const quantity = ref(1)

const show3d = ref(false)

const variants = computed(() => {
  return _product.value.options.filter((e) => e.name != 'Title')
})

const splitContent = computed(() => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(_product.value.descriptionHtml, 'text/html')
  const elements = Array.from(doc.body.children)
  const groups = []
  let currentGroup = null
  let counter = 0

  elements.forEach((element) => {
    if (element.tagName.toLowerCase() === 'h2') {
      if (currentGroup) {
        groups.push(currentGroup)
      }
      currentGroup = { header: element.outerHTML, counter: counter, content: [] }
    } else if (currentGroup) {
      currentGroup.content.push(element.outerHTML)
    }
    counter++
  })

  if (currentGroup) {
    groups.push(currentGroup)
  }
  

  const resultHtml = groups
    .map((group) => {
      // Avoid id-collisions in the ProductPanel-Stack on the same page
      const rnd = Math.floor(Math.random() * 1000000000000).toString(16)
      return `<div class="dropdown"><input class="dropdown-checkbox" type="checkbox"${group.counter == 0 ? ' checked' : ''} id="dd-${rnd}-${group.counter}" /><label class="dropdown-header" for="dd-${rnd}-${group.counter}">${group.header}<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="dropdown-icon"><path d="M12 16.4992L4.5 8.99922L5.55 7.94922L12 14.3992L18.45 7.94922L19.5 8.99922L12 16.4992Z" fill="currentColor"/></svg></label><div class="dropdown-content dd-${rnd}-${group.counter}"><div>${group.content.join('')}</div></div></div>`
    })
    .join('')

  return resultHtml
})

const fetchPrice = (reload = false) => {
  if (!_product.value || !_product.value.id) {
    return
  }

  if (reload) {
    _loading.value = true
  }
  price.value = null
  compareAtPrice.value = null

  let selectedOptions = []
  for (const optionId in options.value) {
    if (Object.prototype.hasOwnProperty.call(options.value, optionId)) {
      const element = options.value[optionId]
      selectedOptions.push(element)
    }
  }

  shopify
    .variantForOptions(_product.value.id, selectedOptions, props.imageWidth, props.imageHeight)
    .then((response) => {
      const productVariant = response.product?.variantBySelectedOptions

      if (!productVariant) {
        availableForSale.value = false
        quantityAvailable.value = 0
        return
      }

      if (productVariant.linkedProduct != null) {
        // FIXME: Maybe we should check for a possible infinite loop here
        _product.value = productVariant.linkedProduct.reference
        setupProduct(true)
      }

      if (productVariant.media != null && productVariant.media.references.edges.length > 0) {
        images.value = []

        for (const key in productVariant.media.references.edges) {
          if (Object.prototype.hasOwnProperty.call(productVariant.media.references.edges, key)) {
            const element = productVariant.media.references.edges[key]
            images.value.push(element.node)
          }
        }
      }

      if (images.value.length > 1) {
        setupSlider()
      }

      availableForSale.value = productVariant.availableForSale
      quantityAvailable.value = productVariant.quantityAvailable
      variant.value = productVariant.id
      price.value = productVariant.price
      compareAtPrice.value = productVariant.compareAtPrice

      _loading.value = false
    })
    .catch((error) => {
      console.error('fetchPrice: Error fetching variant data:', error)
    })
    .finally(() => {
      _loading.value = false
    })
}

const setupSlider = () => {
  sliderReady.value = false;
  if (sliderMain.value) {
    sliderMain.value.destroy(true)
    sliderMain.value = null
  }

  if (sliderMainRef.value == null) {
    return
  }

  sliderMain.value = new Splide(sliderMainRef.value, {
    type: 'loop',
    perPage: 1,
    perMove: 1,
    flickPower: 300,
    flickMaxPages: 1,
    arrows: false,
    mediaQuery: 'min'
  })

  window.setTimeout(() => {
    sliderMain.value.mount();
    sliderReady.value = true;
  }, 50)
}

const fetchModel = async (productId) => {
  const dataStore = useDataStore();
  const rooms = await dataStore.fetchData('rooms', `${asset_host}rooms.json?v=${minuteVersion}`);

  for (const roomId in rooms) {
    if (!Object.prototype.hasOwnProperty.call(rooms, roomId)) {
      continue
    }

    for (const productIndex in rooms[roomId].products) {
      if (rooms[roomId].products[productIndex].shopify_id === productId) {
        return Promise.resolve(rooms[roomId].products[productIndex].model)
      }
    }
  }

  return Promise.reject();
}

const setupProduct = (reload = false) => {
  if (!_product.value) return

  // Clear options and images to avoid stale data
  options.value = {}
  images.value = []

  _product.value.options.forEach((option) => {
    options.value[option.id] = { name: option.name, value: option.values[0] }
  })

  _product.value.media.edges.forEach((media) => {
    images.value.push(media.node)
  })

  // if product model is not defined, fetch it from config
  if (!_product.value.model) {
    fetchModel(_product.value.id)
    .then((model) => {
      _product.value.model = model
    });
  }

  fetchPrice(reload)
}

const fetchProduct = (product) => {
  if (typeof product?.shopifyId != 'undefined') {
    _loading.value = true

    shopify
      .fetchProductById(product.shopifyId, props.imageWidth, props.imageHeight)
      .then((response) => {
        _product.value = response.product
        _product.value.model = product.model
        setupProduct(true)
      })
  } else {
    _product.value = product
    setupProduct(true)
  }
}

const updateOptions = (event) => {
  options.value[event.target.id].value = event.target.value

  if (allOptionsSelected()) {
    fetchPrice()
  }
}

const allOptionsSelected = () => {
  for (const key in options.value) {
    if (options.value[key] === '') {
      return false
    }
  }
  return true
}

function selectProduct(product) {
  trackEvent('select_product', product.handle);
  router.push({ name: 'ProductView', params: { handle: product.handle } });
}

function shareProduct() {
  if (canShare.value) {
    return share(location.href, `wyrld - ${_product.value.title}`);
  } else {
    console.log("Copied link to clipboard");
    navigator.clipboard.writeText(location.href);
  }
}


watch(
  () => props.product,
  (newProduct) => {
    if (newProduct) {
      show3d.value = false
      fetchProduct(newProduct)
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  if (sliderMain.value) {
    sliderMain.value.destroy(true)
  }
})
</script>

<template>
    <div class="product-panel">
      <div class="product__container">

  <div class="product__share" :class="{'product__share--standalone': layoutMode === 'page'}" style="display:none">
    <ButtonDefault
      v-show="!_loading && _product"
      icon="Share"
      icon-only
      @click="shareProduct"
    />
  </div>
  <div class="product" :class="{'product--standalone': layoutMode === 'page'}" ref="container">
    <div class="loader" v-if="_loading">&nbsp;</div>

    <template v-if="!_loading && !_product">
      <p>Produkt nicht gefunden</p>
    </template>

    <div v-show="!_loading && _product" class="product__media-wrapper">
      <div class="product__media">
        <div v-if="show3d" class="product-3d-view">
          <Suspense>
            <ProductRendererStandalone :url="asset_host + (_product.model ?? 'duck.fbx')" />
          </Suspense>
          <ButtonDefault @click="show3d = !show3d" icon-only :icon="show3d.value ? 'BirdsEyeView' : '3DView'" class="product_3d_toggle" />
        </div>
        <div v-show="images.length > 1 && !show3d" class="splide" :class="{'splide--loading': !sliderReady}" ref="sliderMainRef">
          <div class="splide__track">
            <ul class="splide__list">
              <li class="splide__slide" v-for="media in images" :key="`m_${media.id}`">
                <ProductMedia :media="media" :width="imageWidth" :height="imageHeight" />
                <div class="product-unavailable" v-if="!availableForSale">
                  <div class="product-unavailable__label">ausverkauft</div>
                </div>
              </li>
            </ul>
          </div>
          <ButtonDefault @click="show3d = !show3d" icon-only :icon="show3d.value ? 'BirdsEyeView' : '3DView'" class="product_3d_toggle" />
        </div>
        <div v-show="images.length <= 1 && !show3d" class="product__media-single">
          <ProductMedia
            v-for="media in images"
            :key="`m_${media.id}`"
            :media="media"
            :width="imageWidth"
            :height="imageHeight"
          />
        </div>
        <ButtonDefault
          v-if="!_loading && _product && canShare"
          icon="Share"
          icon-only
          class="product__media-share"
          @click="shareProduct"
        />
      </div>
    </div>

    <div v-if="!_loading && _product" class="product__details">
      <div class="product__content">
        <div class="product__title">
          <span v-if="_product.brand" class="product__vendor">{{ _product.brand.value }}</span>
          <h3 class="product__headline">{{ _product.title }}</h3>

          <div class="product__price">
            <PriceDisplay :price="price" :compareAtPrice="compareAtPrice" postfix="inkl. MwSt." :font-weight="400" :font-size="1.5" />
            <ProductQuantityChanger
              v-model="quantity"
              :limitMin="true"
              :showOnMobile="true"
              :showOnDesktop="false"
            />
          </div>
          <span style="display:none" v-if="availableForSale && quantityAvailable < 10">Nur noch wenige verfügbar</span>
        </div>

        <div v-if="_product.alternativeVariantName && _product.alternativeVariantName.value" class="alternative-products">
          <h4>Alternative Ausführung</h4>
          <a @click="fetchProduct(_product.alternativeVariantId.reference)" class="alternative-product">
            <img width="80" :src="_product.alternativeVariantImage.reference.image.url" />
            <label>{{ _product.alternativeVariantName.value }}</label>
          </a>
          <a v-if="_product.alternativeVariant2Name && _product.alternativeVariant2Name.value" @click="fetchProduct(_product.alternativeVariant2Id.reference)" class="alternative-product">
            <img width="80" :src="_product.alternativeVariant2Image.reference.image.url" />
            <label>{{ _product.alternativeVariant2Name.value }}</label>
          </a>
        </div>

        <div v-if="variants.length > 0" class="options">
          <div v-for="variant of variants" :key="variant.id">
            <SelectDropdown
              :id="variant.id"
              :name="variant.name"
              :options="variant.values"
              @change="updateOptions"
            />
          </div>
        </div>
        <div class="description bodytext" v-html="splitContent"></div>
      </div>

    </div>
    <div v-if="!_loading && _product" class="product__related">
      <template v-if="layoutMode === 'page' && screenWidth >= 1024">
        <h3 class="product__related-headline">Weitere Produkte</h3>
        <ProductsScroller :related-id="_product.id" :select-product="selectProduct" />
      </template>
      <ProductsRelated v-else :productId="_product.id" headline="Weitere Produkte" :on-page="layoutMode == 'page'" />
    </div>
  </div>

</div>
</div>

<div v-if="!_loading && _product" class="product-panel-footer" :class="{'product__cart--standalone': layoutMode === 'page'}">
  <transition name="fade">
      <span v-if="productAdded" class="product__cart-message" @click="openCart">
        Produkt wurde dem Warenkorb hinzugefügt
      </span>
    </transition>
    <div class="product__cart-actions">
        <AddToCartButton v-model="quantity" :showOnMobile="false" :showOnDesktop="true" :variant="variant" :product="_product" :available="availableForSale" v-if="variant" />
    </div>
</div>
</template>

<style lang="scss" scoped>
@use '@styles/sass_variables.scss' as *;


.product-panel-footer {
  position: fixed;
  bottom: 15px;
  right: 15px;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  background-color: white;
  height: 80px;
  padding: 0;
  width: calc(100dvw - 30px) !important;
  max-width: 600px;
  z-index: 1000;
}

.alternative-products {
  margin-bottom: 1rem;
  align-items: center;
  gap: 1rem;
}
a.alternative-product {
  display: inline-flex;
  border: 1px solid var(--clr-gray-200);
  border-radius: 20px;
  position: relative;
  margin-right: 1rem;
  cursor: pointer;
}
a.alternative-product img {
  border-radius: 20px;
  display: block;
}
a.alternative-product label {
  padding: 1rem;
  position: absolute;
  bottom: -20px;
  font-size: 12px;
  color: var(--clr-gray-800);
  padding: 0;
  width: 100%;
  text-align: center;
}

.loader {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}

.product-3d-view {
  width: 100%;
  height: 350px;
  background-color: var(--clr-white);
}

.product_3d_toggle {
  bottom: 16px;
  margin-left: auto;
  position: absolute;
  right: 24px;
  display: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease;
}

.fade-enter-active {
  opacity: 0;
}

.fade-enter-to {
  opacity: 1;
}

.related-products {
  background: #f4f5f8;
}




.product-panel {
  overscroll-behavior: none;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 999;

  width: 100dvw;
  max-width: 600px;
  height: 100dvh;
  overflow-y: auto;
  background-color: var(--clr-white);

  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  .header {
    height: 60px;
    position: sticky;
    padding: 0 20px;
    width: 100%;
    z-index: 1000;
    top: 0;
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);

    &__logo {
      position: fixed;
      left: 22px;
      top: 12px;
    }

    &__right {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 32px;
      height: 100%;
    }
  }

  .product__container {
    position: relative;
  }
}

.product-panel {
  right: 15px !important;
  top: 75px !important;
  width: calc(100dvw - 30px) !important;
  height: calc(100dvh - 170px) !important;
}
.product__cart-actions {
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  background-color: white !important;
}


.product {
  height: 100%;
  overflow-y: auto;
  background: var(--clr-white);

  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &--standalone {
    @media screen and (min-width: $breakpoint-large) {
      --max-width: 1392px;
      --col-gap: 96px;
      --child-max-width: calc((var(--max-width) - var(--col-gap)) / 2);
      --scroller-padding: max(20px, (100% - var(--max-width)) / 2);

      position: relative;
      display: grid;
      grid-template-areas: "media details"
                           "related related";
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 96px;
      row-gap: 120px;
      padding-top: 80px;
    }
  }

  &__share {
    position: absolute;
    right: 25px;
    top: 340px;
    height: 60px;
    display: flex;
    align-items: center;
    padding-left: 20px;
    z-index: 1001;

    &--standalone {
      display: none;
    }
  }

  &__media-wrapper {
    grid-area: media;
    .product--standalone & {
      @media screen and (min-width: $breakpoint-large) {
        max-width: var(--child-max-width);
        padding-left: 20px;
        width: 100%;
        justify-self: end;
      }
    }
  }

  &__media {
    position: sticky;
    top: 0;
    background: var(--clr-white);
    max-height: 400px;
    height: 100%;
    // overflow: hidden;
    .product--standalone & {
      @media screen and (min-width: $breakpoint-large) {
        // fill screen minus header, 2 * 80px padding, 80px cart footer
        --max-height: calc(100vh - var(--header-height) - 240px);
        max-height: var(--max-height);
        width: 100%;
        justify-self: end;
        :deep(.preview-image), :deep(.product-media) {
          max-height: var(--max-height);
        }
        :deep(.preview-image) {
          border-radius: 24px;
          object-fit: cover;
        }
        :deep(.product-3d-view) {
          height: var(--max-height);
        }
        :deep(.splide__pagination) {
          bottom: 9px;
        }

        :deep(.product_3d_toggle) {
          bottom: 0;
          right: 0;
          svg {
            width: 32px;
            height: 32px;
          }
        }
      }
    }
  }

  &__media-share {
    display: none;
    position: absolute;
    top: 20px;
    right: 20px;
    width: 40px;
    height: 40px;
    background: var(--clr-white);
    border-radius: 50%;

    .product--standalone & {
      display: flex;
      @media screen and (min-width: $breakpoint-large) {
        right: 32px;
        top: 32px;
        width: 56px;
        height: 56px;

        :deep(svg) {
          width: 28px;
          height: 28px;
        }
      }
    }
  }

  &__title {
    padding-bottom: 32px;
    .product--standalone & {
      @media screen and (min-width: $breakpoint-large) {
        :deep(.price__value), :deep(.price__currency) {
          font-size: 28px;
        }
        :deep(.price__vat) {
          font-size: 16px;
        }
      }
    }
  }

  &__price {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__details {
    grid-area: details;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 24px;

    background: var(--clr-white);
    .product--standalone & {
      @media screen and (min-width: $breakpoint-large) {
        max-width: var(--child-max-width);
        padding-top: 0;
      }
    }

  }

  &__related {
    grid-area: related;
    @media screen and (min-width: $breakpoint-medium) {
      margin-bottom: 24px;
    }
  }

  &__related-headline {
    margin: 24px var(--scroller-padding);
  }

  &__content {
    padding: 20px;
    .product--standalone & {
      @media screen and (min-width: $breakpoint-large) {
        padding: 0 20px 0 0;
      }
    }

    .options {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-bottom: 32px;
      .product--standalone & {
        @media screen and (min-width: $breakpoint-large) {
          :deep(.select) {
            label {
              padding: 14px 17px;
              height: unset;
            }
          }
          :deep(.select__value), :deep(.select__overlay) {
            font-size: 20px;
            line-height: 28px;
          }
          :deep(.select__label) {
            font-size: 14px;
          }
          :deep(.select__button) {
            padding: 14px 17px;
            min-height: 80px;
          }
          :deep(.select__button-text) {
            gap: 0;
          }
        }
      }
    }
  }

  &__cart {
    position: sticky;
    z-index: 4;
    bottom: 0;
    width: 100%;
    height: 80px;

    &--standalone {
      --max-width: 1392px;
      --col-gap: 96px;
      --child-max-width: calc((var(--max-width) - var(--col-gap)) / 2);
      --scroller-padding: max(20px, (100% - var(--max-width)) / 2);
    }

    &-message {
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 14px;
      padding: 14px 32px;
      margin: 16px auto;
      color: var(--clr-white);
      background-color: var(--clr-blue-700);
      border-radius: 100px;
      width: fit-content;
      box-shadow:
        0px 4px 8px 3px rgba(0, 0, 0, 0.15),
        0px 1px 3px 0px rgba(0, 0, 0, 0.3);
      cursor: pointer;
    }

    &-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      background: var(--clr-white);
      padding: 16px var(--padding-inline-page);
      box-shadow: 0 -2px 12px 0 rgba(36, 38, 53, 0.12);

      :deep(.cart-button) {
        flex-grow: 1;
      }
      :deep(.quantity-changer) {
        flex-grow: 1;
      }

      .product__cart--standalone & {
        @media screen and (min-width: $breakpoint-medium) {
          :deep(.cart-button) {
            flex-grow: 0;
            min-width: 188px;
            &[disabled] {
              width: unset;
              margin-inline: 0;
            }
          }
          :deep(.quantity-changer) {
            flex-grow: 0;
            min-width: 188px;
            gap: 32px;
          }
        }
        @media screen and (min-width: $breakpoint-large) {
          padding-inline: max(20px, (100% - var(--max-width)) / 2 + 20px);
          gap: 20px;
          justify-content: flex-end;
        }
      }
    }
  }

  &__vendor {
    color: var(--clr-brand-500);
    font-size: 20px;
    line-height: 24px; /* 120% */
    padding-bottom: 4px;
    font-family: "Cera GR";
    font-style: normal;
    font-weight: 500;
    line-height: 28px; /* 140% */
    text-transform: uppercase;
    .product--standalone & {
      @media screen and (min-width: $breakpoint-large) {
        font-size: 16px;
        padding: 0;
        margin-bottom: -2px;
        line-height: 1;
      }
    }
  }

  &__headline {
    font-family: "Open Sans";
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px;
    padding-bottom: 12px;
    .product--standalone & {
      @media screen and (min-width: $breakpoint-large) {
        font-size: 28px;
        line-height: 46px;
      }
    }
  }
}

.product-unavailable {
  position: absolute;
  inset: 0;
  background-color: rgba(255, 255, 255, 0.65);

  &__label {
    position: absolute;
    bottom: 24px;
    left: 24px;
    background: rgba(255, 195, 186, 1);
    color: rgba(199, 73, 62, 1);
    font-weight: 600;
    padding: 8px 20px;
    border-radius: 100px;
  }
}
</style>

<style lang="scss">

.splide {
  padding-bottom: 48px;
  overflow: hidden;
  visibility: visible;

  &:not(.is-overflow) &__pagination {
    display: flex;
  }

  &--loading {
    visibility: hidden;
  }
}

.splide__pagination {
  bottom: 20px;
  padding-inline: 48px;

  &__page {
    &.is-active {
      background-color: var(--clr-brand-500);
      transform: unset;
    }
  }
}
// Dropdown styling
// We need to do this in an unscoped block, since scoped styles in Vue are only applied
// to the elements within the component's template.
.dropdown {
  border-bottom: 1px solid var(--clr-gray-50);

  &-checkbox {
    display: none;

    &:checked {
      & ~ .dropdown-content {
        grid-template-rows: 1fr;
      }

      & ~ .dropdown-header > .dropdown-icon {
        transform: rotate(180deg);
      }
    }
  }

  &-icon {
    transition: transform 0.4s ease-in-out;
  }

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 24px 16px;

    h2 {
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 1.5;

      > * {
        font-weight: 600;
      }
    }

  }

  &-content {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.4s ease-in-out;
    font-size: 0.875rem;
    padding-inline: 16px;

    & > div {
      overflow: hidden;

      > *:last-child {
        padding-bottom: 24px;
      }
    }

    p + p {
      margin-bottom: 16px;
    }
    ul {
      margin-block: 0;
    }
  }
}

/* PDP PopUp */
.product__cart {
  position: fixed !important;
  bottom: 15px !important;
  right: 15px !important;  
  width: calc(100dvw - 30px) !important;
  max-width: 600px !important;
}
</style>
